import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Minimize2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '@/context/AppContext';
import axios from 'axios';

const getBackendUrl = () => {
    if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.startsWith('http')) {
        return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
    }
    return '';
};

const getWsBaseUrl = () => {
    if (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.startsWith('http')) {
        return process.env.REACT_APP_BACKEND_URL.replace('http://', 'ws://').replace('https://', 'wss://').replace(/\/$/, '');
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}`;
};

const BACKEND_URL = getBackendUrl();
const WS_BASE_URL = getWsBaseUrl();

export const ChatWidget = () => {
    const { user } = useApp();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [sessionInfo, setSessionInfo] = useState(() => {
        try {
            const saved = localStorage.getItem('gpgt_chat_session');
            return saved ? JSON.parse(saved) : null;
        } catch { return null; }
    });

    const [messages, setMessages] = useState([]);
    const [inputMsg, setInputMsg] = useState('');
    const [loading, setLoading] = useState(false);

    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [isAdminOnline, setIsAdminOnline] = useState(true);

    const ws = useRef(null);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);
    const sessionRef = useRef(sessionInfo);
    const isOpenRef = useRef(isOpen);

    useEffect(() => { sessionRef.current = sessionInfo; }, [sessionInfo]);
    useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
    };

    useEffect(() => { scrollToBottom(); }, [messages, isOpen]);

    const fetchAdminStatus = async () => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/chat/admin/status`);
            setIsAdminOnline(data.online);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        fetchAdminStatus();
    }, []);

    const addMessages = useCallback((newMsgs) => {
        setMessages(prev => {
            const ids = new Set(prev.map(m => m.id));
            const fresh = newMsgs.filter(m => !ids.has(m.id));
            if (fresh.length === 0) return prev;
            
            // Remove local optimistic messages that match the incoming fresh messages
            let currentMessages = [...prev];
            fresh.forEach(incoming => {
                if (!incoming.is_admin) {
                    const localIdx = currentMessages.findIndex(m => m.id.startsWith('local_') && m.content === incoming.content);
                    if (localIdx !== -1) {
                        currentMessages.splice(localIdx, 1);
                    }
                }
            });

            // Count admin replies as new unread if widget is closed
            if (!isOpenRef.current) {
                const adminCount = fresh.filter(m => m.is_admin).length;
                if (adminCount > 0) setUnread(u => u + adminCount);
            }
            return [...currentMessages, ...fresh];
        });
    }, []);

    // REST polling — fallback when WS disconnects
    const startPolling = useCallback((sessionId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/chat/sessions/${sessionId}`);
                addMessages(data.messages || []);
            } catch (e) { /* ignore */ }
        }, 5000);
    }, [addMessages]);

    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const connectWebSocket = useCallback((sessionId) => {
        if (ws.current) ws.current.close();

        const clientId = Math.random().toString(36).substring(7);
        const wsUrl = `${WS_BASE_URL}/api/chat/ws/${clientId}/${sessionId}`;

        const socket = new WebSocket(wsUrl);

        socket.onopen = () => {
            stopPolling(); // WS connected — stop polling
        };

        socket.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === 'presence' && msg.role === 'admin') {
                    setIsAdminOnline(msg.online);
                    return;
                }
                addMessages([msg]);
            } catch (e) { /* ignore */ }
        };

        socket.onclose = () => {
            // WS dropped — start REST polling as fallback
            if (sessionRef.current) startPolling(sessionId);
            // Try to reconnect after 5s
            setTimeout(() => {
                if (sessionRef.current) connectWebSocket(sessionId);
            }, 5000);
        };

        ws.current = socket;
    }, [addMessages, startPolling]);

    const fetchHistory = async (sessionId) => {
        try {
            const { data } = await axios.get(`${BACKEND_URL}/api/chat/sessions/${sessionId}`);
            setMessages(data.messages || []);
        } catch (e) { /* ignore */ }
    };

    useEffect(() => {
        if (sessionInfo && isOpen) {
            fetchHistory(sessionInfo.id);
            connectWebSocket(sessionInfo.id);
            setUnread(0);
        }
        return () => {
            if (ws.current) ws.current.close();
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionInfo, isOpen]);

    const startChat = async (e) => {
        e.preventDefault();
        if (!name || !email) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${BACKEND_URL}/api/chat/sessions`, {
                customer_name: name,
                customer_email: email
            });
            setSessionInfo(data);
            localStorage.setItem('gpgt_chat_session', JSON.stringify(data));
            setMessages(data.messages || []);
            connectWebSocket(data.id);
        } catch (error) {
            console.error('[Chat] Failed to start chat', error);
        }
        setLoading(false);
    };

    const sendMessage = (e) => {
        e.preventDefault();
        const text = inputMsg.trim();
        if (!text || !sessionInfo) return;

        const localMsg = {
            id: 'local_' + Date.now(),
            session_id: sessionInfo.id,
            content: text,
            is_admin: false,
            timestamp: new Date().toISOString()
        };
        setMessages(prev => [...prev, localMsg]);
        setInputMsg('');

        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            ws.current.send(text);
        } else {
            // REST fallback
            axios.post(`${BACKEND_URL}/api/chat/sessions/${sessionInfo.id}/messages`, {
                content: text,
                is_admin: false
            }).catch(err => console.error('[Chat] REST fallback failed:', err));
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        setUnread(0);
    };

    // Don't show in admin panel
    if (location.pathname.startsWith('/admin') || window.location.hash.startsWith('#/admin')) return null;

    return (
        <>
            {/* Floating Button — positioned above WhatsApp button */}
            {!isOpen && (
                <button
                    onClick={handleOpen}
                    className="fixed z-50 flex items-center justify-center bg-[#0c1b44] hover:bg-[#1a2d64] text-white rounded-full shadow-2xl w-14 h-14 transition-all hover:scale-105 active:scale-95 bottom-[148px] right-4 md:bottom-[96px] md:right-6"
                >
                    <MessageCircle className="h-7 w-7" />
                    {unread > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center">
                            {unread}
                        </span>
                    )}
                </button>
            )}

            {/* Chat Window */}
            {isOpen && (
                <div
                    className="fixed z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 bottom-[136px] right-4 md:bottom-[88px] md:right-6 w-[calc(100vw-32px)] md:w-[370px] h-[60vh] md:h-[520px] max-h-[80vh]"
                >
                    {/* Header */}
                    <div className="bg-[#0c1b44] p-4 flex justify-between items-center text-white flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center">
                                <MessageCircle className="h-5 w-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-bold text-sm leading-none">GPGT Support</h3>
                                <p className="text-xs text-white/60 mt-0.5 flex items-center gap-1">
                                    <span className={`h-1.5 w-1.5 rounded-full ${isAdminOnline ? 'bg-green-400' : 'bg-gray-400'} inline-block`}></span>
                                    {isAdminOnline ? 'Online · Typically replies instantly' : 'Offline · We will reply as soon as possible'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="p-2 hover:bg-white/10 rounded-full transition-colors"
                        >
                            <ChevronDown className="h-5 w-5" />
                        </button>
                    </div>

                    {!sessionInfo ? (
                        /* Registration Form */
                        <div className="flex-1 p-6 flex flex-col justify-center bg-gray-50 overflow-y-auto">
                            <div className="text-center mb-6">
                                <div className="h-16 w-16 bg-[#0c1b44]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <MessageCircle className="h-7 w-7 text-[#0c1b44]" />
                                </div>
                                <h4 className="text-lg font-bold text-[#0c1b44] mb-1">Welcome! 👋</h4>
                                <p className="text-sm text-gray-500">Fill in your details to chat with our team.</p>
                            </div>
                            <form onSubmit={startChat} className="space-y-3">
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</label>
                                    <Input required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="mt-1" />
                                </div>
                                <div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</label>
                                    <Input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="mt-1" />
                                </div>
                                <Button type="submit" className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white font-bold mt-2" disabled={loading}>
                                    {loading ? 'Starting...' : 'Start Chat →'}
                                </Button>
                            </form>
                        </div>
                    ) : (
                        /* Chat Interface */
                        <>
                            <ScrollArea className="flex-1 bg-gray-50/50">
                                <div className="p-4 space-y-3">
                                    <div className="flex justify-center">
                                        <span className="text-xs text-gray-400 bg-white border border-gray-100 px-3 py-1 rounded-full shadow-sm">
                                            Chat started
                                        </span>
                                    </div>

                                    {messages.map((msg) => (
                                        <div key={msg.id} className={`flex items-end gap-2 ${msg.is_admin ? 'justify-start' : 'justify-end'}`}>
                                            {msg.is_admin && (
                                                <div className="h-6 w-6 rounded-full bg-[#0c1b44] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                                    GP
                                                </div>
                                            )}
                                            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                                                msg.is_admin
                                                    ? 'bg-white border border-gray-100 text-gray-800 rounded-tl-none'
                                                    : 'bg-[#0c1b44] text-white rounded-tr-none'
                                            }`}>
                                                {msg.is_admin && <div className="text-[10px] font-bold text-[#0c1b44] mb-1">GPGT Support</div>}
                                                <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                                                <div className={`text-[10px] mt-1 text-right ${msg.is_admin ? 'text-gray-400' : 'text-white/50'}`}>
                                                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                            </ScrollArea>

                            {/* Input */}
                            <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
                                <form onSubmit={sendMessage} className="flex gap-2 items-center">
                                    <Input
                                        value={inputMsg}
                                        onChange={(e) => setInputMsg(e.target.value)}
                                        placeholder="Type a message..."
                                        className="flex-1 bg-gray-50 border-gray-200 rounded-full text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#0c1b44] outline-none"
                                    />
                                    <Button
                                        type="submit"
                                        size="icon"
                                        className="h-10 w-10 rounded-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white flex items-center justify-center flex-shrink-0"
                                        disabled={!inputMsg.trim()}
                                    >
                                        <Send className="h-4 w-4" />
                                    </Button>
                                </form>
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default ChatWidget;
